<script>
    import { onMount } from "svelte";
    import Button from "../../ui_elements/Button.svelte";
    import { PDFDocument, rgb } from "pdf-lib";
    
    // example: rows = [{ label1: "Ausbildung", score: 0.8, label2: "Studium" }, {...}]
    export let rows = [];
    // A dictionary containing the name of the left column and defining both ends of the score line. Providing a topRow is optional.
    // example: topRow = { columnName: "Fähigkeit", leftLabel: "kann ich nicht so gut", rightLabel: "kann ich sehr gut"}
    export let topRow = {};
    export let rightSideWidth = 10;

    // display controls whether the chart is visible ('block') or not ('hidden'). Hiding the chart is done in instances where only the export functionality is required. In that case, only the export button is shown.
    export let display = "block";

    // If this flag is set, a PNG of the chart can be saved by clicking the download button beneath the chart. When the export button is clicked, a second SVG with the dimension specified in the \lstinline{exportWidth} parameter is shortly displayed and converted to a PNG by serializing the SVG, creating an image from it and drawing it onto a canvas. The canvas is then converted into a temporary URL and a click on the temporary URL link element triggers the download.
    export let exportable = false;
    export let exportWidth = 1000;
    // If the bescheinigung flag is set, a button for exporting the participation certificate is displayed below the chart. fullName should contain the user's name to be used in the certificate. 
    export let bescheinigung = false;
    export let fullName = "";
    let date = new Date().toLocaleDateString();

    let svgElement; 
    let svgElementExport;
    let current_width = 800; // default value, but is dynamically changed.
    let labelWidth = 80;
    let topHeight = 30;

    function updateWidth() {
        current_width = svgElement.clientWidth;
    }

    // onMount is needed otherwise svgElement isn't defined yet
    onMount(() => {
        updateWidth();
        window.addEventListener('resize', updateWidth);

        const longestLabel1 = rows.reduce((max, row) => { return row.label1.length > max ? row.label1.length : max }, 0);
        const longestLabel2 = rows.reduce((max, row) => { return row.label2.length > max ? row.label2.length : max }, 0);
        const longestLabel = Math.max(longestLabel1, longestLabel2);
        labelWidth = longestLabel * 8 + 15;

        if (Object.keys(topRow).length == 0){
            topHeight = 0;
        }

        if (rows[0].label2) {
            rightSideWidth = labelWidth;
        }
        return () => {
            window.removeEventListener('resize', updateWidth);
        };
    });

    
    export async function exportCertificate() {
        const existingPdfBytes = await fetch('teilnahmebescheinigung_template.pdf').then(res => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const [page1] = pdfDoc.getPages();
        const { width, height } = page1.getSize();
        
        let textWidth = fullName.length * 8
        page1.drawText(fullName, {
            x: (width - textWidth)/2,
            y: height - 300,
            size: 20,
            color: rgb(0, 0, 0),
        });

        textWidth = `am ${date}`.length * 8
        page1.drawText(`am ${date}`, {
            x: (width - textWidth)/2,
            y: height - 210,
            size: 15,
            color: rgb(0, 0, 0),
        });

        const page2 = pdfDoc.addPage();
        const pngUrl = await getPngUrl();
        const pngImageBytes = await fetch(pngUrl).then(res => res.arrayBuffer());
        const pngImage = await pdfDoc.embedPng(pngImageBytes);
        const pngDims = pngImage.scale(0.4);

        page2.drawImage(pngImage, {
            x: (page2.getWidth() - pngDims.width) / 2,
            y: page2.getHeight() / 2 - pngDims.height / 2,
            width: pngDims.width,
            height: pngDims.height,
        });

        page2.drawText('Stärkenprofil', {
            x: 50,
            y: height-30,
            size: 20,
            color: rgb(0, 0, 0),
        });

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Teilnahmebescheinigung.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    export async function getPngUrl() {
        return new Promise((resolve) => {
            const serializer = new XMLSerializer();
            svgElementExport.style.display = 'block';
            const source = serializer.serializeToString(svgElementExport);
            const svgData = `<?xml version="1.0" standalone="no"?>\r\n${source}`;

            const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = async () => {
                const canvas = document.createElement('canvas');
                canvas.width = svgElementExport.clientWidth;
                canvas.height = svgElementExport.clientHeight; 
                const context = canvas.getContext('2d');

                context.drawImage(img, 0, 0);

                const pngUrl = canvas.toDataURL('image/png');
                URL.revokeObjectURL(url);
                svgElementExport.style.display = 'none';
                resolve(pngUrl);
            };
            img.src = url;
        });
        
    }

    async function saveAsPng() {
        const a = document.createElement('a');
        a.href = await getPngUrl();
        a.download = 'chart.png';
        a.click();
    }

</script>

<div class="w-full mt-2 text-secondary-text" style="display: {display}">
    <svg bind:this={svgElement} width=100% height="{rows.length * 40 + topHeight}">
        <style>
            text {
                fill: #262626;
            }
        </style>

        <rect 
            x=0
            y=0
            width={current_width}
            height={rows.length * 40 + topHeight}
            fill="white"
        />

        {#if topHeight > 0}
            <text x=10 y=20 class="uppercase text-sm font-bold">{topRow.columnName}</text>
            <text x={labelWidth} y=20 class="uppercase text-sm font-bold">{topRow.leftLabel}</text>
            <text x={current_width - rightSideWidth} y=20 class="uppercase text-sm font-bold" text-anchor="end">{topRow.rightLabel}</text>
        {/if}

        {#each rows as { label1, score, label2 }, index}
        
            <rect 
                x=0
                y={index * 40 + topHeight} 
                width={current_width} 
                height=40
                class='{index % 2 === 0 ? 'fill-accent-light' : 'fill-accent-very-light'} hover:fill-accent-hover'
            />

            <line
                x1={labelWidth}
                y1={topHeight + 20 + index * 40}
                x2={current_width - rightSideWidth}
                y2={topHeight + 20 + index * 40}
                stroke='#262626'
                stroke-width=2
            />
            <circle
                cx={labelWidth + score * (current_width - labelWidth - rightSideWidth)}
                cy={topHeight + 20 + index * 40}
                r=8
                fill='#262626'
            />

            {#if index > 0}
                <line
                    x1={labelWidth + rows[index - 1].score * (current_width - labelWidth - rightSideWidth)}
                    y1={topHeight + 20 + (index - 1) * 40}
                    x2={labelWidth + score * (current_width - labelWidth - rightSideWidth)}
                    y2={topHeight + 20 + index * 40}
                    stroke='#262626'
                    stroke-width=2
                />
            {/if}

            <text x=10 y={topHeight + 20 + 5 + index * 40} class="text-md">{label1}</text>
            <text x={current_width - rightSideWidth + 10} y={topHeight + 20 + 5 + index * 40} class="text-md">{label2}</text>
        {/each}
    </svg>

    {#if exportable}
        <Button on:click={saveAsPng} variant="submit" className="ml-auto mt-2">Graph speichern</Button>
    {/if}
</div>
{#if bescheinigung}
    <Button on:click={exportCertificate} variant="submit">Teilnahmebescheinigung herunterladen</Button>
{/if}


<!-- seperate svg for export to allow the other one to dynamically change size and have hover effects -->
<svg bind:this={svgElementExport} width={exportWidth} height="{rows.length * 40 + topHeight}" style="font-family: Arial, sans-serif; display: none;">
    <style>
        text {
            fill: #262626;
        }
    </style>

    <rect 
        x=0
        y=0
        width={exportWidth}
        height={rows.length * 40 + topHeight}
        fill="white"
    />

    {#if topHeight > 0}
        <text x=10 y=20 class="text-label">{topRow.columnName}</text>
        <text x={labelWidth} y=20 class="text-label">{topRow.leftLabel}</text>
        <text x={exportWidth - rightSideWidth} y=20 class="text-label" text-anchor="end">{topRow.rightLabel}</text>
    {/if}

    {#each rows as { label1, score, label2 }, index}
        <rect 
            x=0
            y={index * 40 + topHeight} 
            width={exportWidth} 
            height=40
            fill={index % 2 === 0 ? '#fef3c7' : '#fffbeb'}
        />

        <line
            x1={labelWidth}
            y1={topHeight + 20 + index * 40}
            x2={exportWidth - rightSideWidth}
            y2={topHeight + 20 + index * 40}
            stroke='#262626'
            stroke-width=2
        />
        <circle
            cx={labelWidth + score * (exportWidth - labelWidth - rightSideWidth)}
            cy={topHeight + 20 + index * 40}
            r=8
            fill='#262626'
        />

        {#if index > 0}
            <line
                x1={labelWidth + rows[index - 1].score * (exportWidth - labelWidth - rightSideWidth)}
                y1={topHeight + 20 + (index - 1) * 40}
                x2={labelWidth + score * (exportWidth - labelWidth - rightSideWidth)}
                y2={topHeight + 20 + index * 40}
                stroke='#262626'
                stroke-width=2
            />
        {/if}

        <text x=10 y={topHeight + 20 + 5 + index * 40} class="text-md">{label1}</text>
        <text x={exportWidth - rightSideWidth + 10} y={topHeight + 20 + 5 + index * 40} class="text-md">{label2}</text>
    {/each}
</svg>